/**
 * RealtimeWebSocketService for handling WebSocket connections to Maitai transcription service
 * Supports Deepgram Nova-3 and other models via the Maitai proxy
 */

import GeneralService from "./GeneralService";

export interface TranscriptionConfig {
  model: string;
  prompt?: string;
  language?: string;
}

export interface VADConfig {
  type: "server_vad";
  threshold: number;
  prefix_padding_ms: number;
  silence_duration_ms: number;
}

export interface SessionConfig {
  type: "transcription_session.update";
  session: {
    input_audio_format: "pcm16";
    input_audio_transcription: TranscriptionConfig;
    turn_detection: VADConfig;
    input_audio_noise_reduction: {
      type: "near_field";
    };
    include: string[];
  };
}

export interface TranscriptionEvent {
  type: string;
  transcript?: string;
  delta?: string;
  item_id?: string;
  error?: any;
}

export interface RealtimeWebSocketCallbacks {
  onTranscriptionDelta?: (text: string) => void;
  onTranscriptionComplete?: (text: string) => void;
  onVADEvent?: (event: "speech_started" | "speech_stopped", itemId?: string) => void;
  onError?: (error: string) => void;
  onSessionCreated?: (sessionId?: string) => void;
  onConnectionEstablished?: () => void;
  onConnectionClosed?: () => void;
}

export class RealtimeWebSocketService {
  private websocket: WebSocket | null = null;
  private callbacks: RealtimeWebSocketCallbacks = {};
  private isConnected = false;
  private sessionCreated = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private apiKey: string | undefined;

  constructor(callbacks: RealtimeWebSocketCallbacks = {}) {
    this.callbacks = callbacks;
  }

  /**
   * Connect to the Maitai transcription WebSocket service
   */
  async connect(apiKey?: string): Promise<void> {
    if (this.websocket && this.isConnected) {
      console.log("✅ WebSocket is already connected, reusing existing connection");
      return;
    }

    // If there's an existing websocket but it's not connected, clean it up
    if (this.websocket) {
      console.log("🧹 Cleaning up existing disconnected WebSocket");
      this.websocket.close();
      this.websocket = null;
      this.isConnected = false;
      this.sessionCreated = false;
    }

    // Get API key from environment if not provided
    const finalApiKey = apiKey || import.meta.env.VITE_MAITAI_API_KEY;
    this.apiKey = finalApiKey; // Store for reconnection
    
    // Build WebSocket URL with demo mode for breeze demo (no auth required)
    let wsUrl = `${GeneralService.TRANSCRIPTION_SERVICE_ENDPOINT}/realtime?intent=transcription&demo_mode=true`;
    if (finalApiKey) {
      wsUrl += `&api_key=${encodeURIComponent(finalApiKey)}`;
    }
    
    console.log(`Connecting to Maitai transcription service: ${wsUrl.replace(/api_key=[^&]*/, "api_key=***")}`);

    try {
      this.websocket = new WebSocket(wsUrl);

      this.websocket.onopen = () => {
        console.log("✅ Maitai transcription WebSocket connected");
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.callbacks.onConnectionEstablished?.();
      };

      this.websocket.onmessage = (event) => {
        this.handleMessage(event.data);
      };

             this.websocket.onclose = (event) => {
         console.log("❌ Maitai transcription WebSocket disconnected:", event.code, event.reason);
         this.isConnected = false;
         this.sessionCreated = false;
         this.callbacks.onConnectionClosed?.();
         
         // Only attempt to reconnect for unexpected closures and if we haven't manually disconnected
         if (event.code !== 1000 && event.code !== 1001 && this.reconnectAttempts < this.maxReconnectAttempts && this.websocket !== null) {
           this.attemptReconnect();
         }
       };

      this.websocket.onerror = (error) => {
        console.error("❌ Maitai transcription WebSocket error:", error);
        this.callbacks.onError?.("WebSocket connection error");
      };

    } catch (error) {
      console.error("❌ Failed to create WebSocket connection:", error);
      throw new Error(`Failed to connect to transcription service: ${error}`);
    }
  }

  /**
   * Attempt to reconnect to the WebSocket service
   */
  private attemptReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 10000);
    
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.connect(this.apiKey).catch(error => {
        console.error("❌ Reconnection failed:", error);
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          this.callbacks.onError?.("Max reconnection attempts reached");
        }
      });
    }, delay);
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: string): void {
    try {
      const message: TranscriptionEvent = JSON.parse(data);
      const messageType = message.type;

      // Log important messages for debugging
      if (messageType !== "input_audio_buffer.committed") {
        console.log(`Received: ${messageType}`);
      }

      switch (messageType) {
        case "session.created":
          this.sessionCreated = true;
          console.log("✅ Maitai transcription session created", message);
          this.callbacks.onSessionCreated?.(message.item_id);
          break;

        case "conversation.item.input_audio_transcription.delta":
          if (message.delta) {
            console.log(`Delta: ${message.delta}`);
            this.callbacks.onTranscriptionDelta?.(message.delta);
          }
          break;

        case "conversation.item.input_audio_transcription.completed":
          if (message.transcript) {
            console.log(`Completed: ${message.transcript}`);
            this.callbacks.onTranscriptionComplete?.(message.transcript);
          }
          break;

        case "conversation.item.input_audio_transcription.failed":
          console.error("❌ Transcription failed:", message.error);
          this.callbacks.onError?.(`Transcription failed: ${JSON.stringify(message.error)}`);
          break;

        case "input_audio_buffer.speech_started":
          console.log(`Speech started: ${message.item_id || "unknown"}`);
          this.callbacks.onVADEvent?.("speech_started", message.item_id);
          break;

        case "input_audio_buffer.speech_stopped":
          console.log(`Speech stopped: ${message.item_id || "unknown"}`);
          this.callbacks.onVADEvent?.("speech_stopped", message.item_id);
          break;

        case "input_audio_buffer.committed":
          // VAD committed event - usually just indicates buffer processing
          break;

        case "error":
          const errorMsg = typeof message.error === "string" 
            ? message.error 
            : message.error?.message || "Unknown error";
          
          // Suppress "transcription session not found" errors during initial streaming
          // These can occur when audio starts being sent before the backend processes the session config
          if (errorMsg.includes("transcription session not found")) {
            console.warn("⚠️ Suppressed expected error during streaming setup:", errorMsg);
            // Don't call the error callback for this expected error
          } else {
            console.error("❌ Server error:", errorMsg);
            this.callbacks.onError?.(errorMsg);
          }
          break;

        case "transcription_session.created":
        case "transcription_session.updated":
          console.log(`Session event: ${messageType}`);
          break;

        default:
          if (messageType.includes("transcription") || messageType.includes("audio")) {
            console.log(`Unknown transcription/audio message: ${messageType}`);
            console.log("Content:", message);
          }
          break;
      }
    } catch (error) {
      console.error("❌ Failed to parse WebSocket message:", error);
      console.error("Raw message:", data);
    }
  }

  /**
   * Configure the transcription session with the specified model and settings
   */
  async configureSession(config: {
    model: string;
    prompt?: string;
    language?: string;
    vadThreshold?: number;
    prefixPaddingMs?: number;
    silenceDurationMs?: number;
  }): Promise<void> {
    console.log("🔧 Starting session configuration...", { 
      isConnected: this.isConnected, 
      hasWebSocket: !!this.websocket,
      websocketReadyState: this.websocket?.readyState,
      sessionCreated: this.sessionCreated 
    });

    // Check both our internal state and the actual WebSocket state
    // Add a small retry mechanism in case of timing issues
    let retries = 3;
    while (retries > 0 && (!this.websocket || this.websocket.readyState !== WebSocket.OPEN)) {
      console.log(`⏳ WebSocket not ready, waiting... (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, 100));
      retries--;
    }

    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.error("❌ WebSocket is not ready after retries:", {
        hasWebSocket: !!this.websocket,
        readyState: this.websocket?.readyState,
        isConnected: this.isConnected
      });
      throw new Error("WebSocket is not connected or ready");
    }

    // Wait for session creation if not already created
    if (!this.sessionCreated) {
      console.log("⏳ Waiting for session creation...");
      await this.waitForSessionCreation();
      console.log("✅ Session creation completed");
    }

    // Clean up model name - remove provider prefix if present
    const cleanModel = config.model.startsWith("deepgram:") 
      ? config.model.replace("deepgram:", "") 
      : config.model;

    const sessionConfig: SessionConfig = {
      type: "transcription_session.update",
      session: {
        input_audio_format: "pcm16",
        input_audio_transcription: {
          model: cleanModel,
          prompt: config.prompt || "",
        },
        turn_detection: {
          type: "server_vad",
          threshold: config.vadThreshold || 0.5,
          prefix_padding_ms: config.prefixPaddingMs || 300,
          silence_duration_ms: config.silenceDurationMs || 500,
        },
        input_audio_noise_reduction: {
          type: "near_field",
        },
        include: ["item.input_audio_transcription.logprobs"],
      },
    };

    // Add language if specified
    if (config.language) {
      sessionConfig.session.input_audio_transcription.language = config.language;
    }

    console.log("🚀 Sending session configuration:", cleanModel);
    console.log("📋 Session config:", JSON.stringify(sessionConfig, null, 2));
    
    try {
      this.websocket.send(JSON.stringify(sessionConfig));
      console.log("✅ Session configuration sent successfully");
    } catch (error) {
      console.error("❌ Failed to send session configuration:", error);
      throw error;
    }
    
    // Note: No additional start message needed - the session configuration 
    // with VAD settings is sufficient to start the transcription session
  }

  /**
   * Wait for session creation with timeout
   */
  private waitForSessionCreation(timeoutMs: number = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.sessionCreated) {
        console.log("✅ Session already created, proceeding...");
        resolve();
        return;
      }

      console.log(`⏰ Starting session creation wait (timeout: ${timeoutMs}ms)`);
      
      const timeout = setTimeout(() => {
        console.error("❌ Session creation timeout after", timeoutMs, "ms");
        reject(new Error("Session creation timeout"));
      }, timeoutMs);

      const checkSession = () => {
        if (this.sessionCreated) {
          console.log("✅ Session created, clearing timeout");
          clearTimeout(timeout);
          resolve();
        } else {
          setTimeout(checkSession, 100);
        }
      };

      checkSession();
    });
  }

  /**
   * Send audio data to the transcription service
   */
  async sendAudio(audioData: ArrayBuffer): Promise<void> {
    if (!this.isConnected || !this.websocket) {
      throw new Error("WebSocket is not connected");
    }

    // Convert ArrayBuffer to base64
    const uint8Array = new Uint8Array(audioData);
    const audioBase64 = this.arrayBufferToBase64(uint8Array);

    const audioMessage = {
      type: "input_audio_buffer.append",
      audio: audioBase64,
    };

    this.websocket.send(JSON.stringify(audioMessage));
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: Uint8Array): string {
    let binary = "";
    const len = buffer.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(buffer[i]);
    }
    return btoa(binary);
  }

  /**
   * Disconnect from the WebSocket service
   */
  disconnect(): void {
    console.log("🔌 Disconnecting WebSocket service...");
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.websocket) {
      // Set to null first to prevent reconnection attempts
      const ws = this.websocket;
      this.websocket = null;
      this.isConnected = false;
      this.sessionCreated = false;
      
      ws.close(1000, "Normal closure");
    }

    this.reconnectAttempts = 0;
  }

  /**
   * Check if the service is connected
   */
  isWebSocketConnected(): boolean {
    return this.isConnected && this.websocket?.readyState === WebSocket.OPEN;
  }

  /**
   * Update callbacks
   */
  updateCallbacks(callbacks: RealtimeWebSocketCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }
}

export default RealtimeWebSocketService;
